import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowRightLeft, Loader2, Clock, MapPin, Info } from "lucide-react";
import { useLang } from "../i18n/LangProvider";
import { TID } from "../lib/testIds";
import { toast } from "sonner";
import { PricingBreakdown } from "./PricingBreakdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const nowLocalIso = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

export const PriceCalculator = ({ compact = false }) => {
  const { t } = useLang();
  const navigate = useNavigate();

  const [cities, setCities] = useState([]);
  const [origin, setOrigin] = useState("Zürich Airport (ZRH)");
  const [destination, setDestination] = useState("Luzern (Lucerne)");
  const [when, setWhen] = useState(nowLocalIso());
  const [service, setService] = useState("transfer");
  const [childSeats, setChildSeats] = useState(0);
  const [pet, setPet] = useState("none");

  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/cities`).then((r) => setCities(r.data)).catch(() => {});
  }, []);

  const swap = () => {
    setOrigin(destination);
    setDestination(origin);
  };

  const calculate = async () => {
    if (!origin || !destination || origin === destination) {
      toast.error("Please choose different origin and destination.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/quote`, {
        origin, destination,
        when: new Date(when).toISOString(),
        service,
        child_seats: Number(childSeats) || 0,
        pet,
      });
      setQuote(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to compute quote.");
    } finally {
      setLoading(false);
    }
  };

  const compareRows = useMemo(() => {
    if (!quote) return [];
    const our = quote.swissalpenline.total_chf;
    const b = quote.benchmarks;
    const rows = [{ key: "us", label: t.calc.us, value: our, highlight: true, testid: TID.calc.ourPrice }];
    // Only show benchmarks that are actually present (server hides cheaper ones)
    if (b.taxi_chf != null) rows.push({ key: "taxi", label: t.calc.taxi, value: b.taxi_chf, testid: TID.calc.benchmarkTaxi });
    if (b.uber_black_chf != null) rows.push({ key: "uber", label: t.calc.uber, value: b.uber_black_chf, testid: TID.calc.benchmarkUber });
    if (b.other_chauffeur_chf != null) rows.push({ key: "other", label: t.calc.other, value: b.other_chauffeur_chf, testid: TID.calc.benchmarkOther });
    if (b.train_chf != null) rows.push({ key: "train", label: t.calc.train, value: b.train_chf, testid: TID.calc.benchmarkTrain });
    return rows;
  }, [quote, t]);

  const reserveFromQuote = () => {
    if (!quote) return;
    const params = new URLSearchParams({
      service,
      origin,
      destination,
      when: new Date(when).toISOString(),
      child_seats: String(childSeats),
      pet,
      total: String(quote.swissalpenline.total_chf),
      distance_km: String(quote.distance_km),
    });
    navigate(`/reservar?${params.toString()}`);
  };

  return (
    <div className={`bg-brand-surface border border-brand-border ${compact ? "p-6" : "p-8 lg:p-10"}`}>
      <div className="mb-8">
        <p className="text-xs tracking-widest2 uppercase text-brand-silver mb-2">{t.calc.title}</p>
        <h3 className="text-2xl lg:text-3xl font-light text-brand-platinum">{t.calc.subtitle}</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-end">
        <label className="flex flex-col gap-2">
          <span className="text-[10px] tracking-widest2 uppercase text-brand-silver flex items-center gap-2">
            <MapPin size={12} /> {t.calc.origin}
          </span>
          <select
            value={origin}
            onChange={(e) => setOrigin(e.target.value)}
            data-testid={TID.calc.origin}
            className="bg-brand-bg border border-brand-border text-brand-platinum px-4 py-3 text-sm focus:border-brand-platinum"
          >
            {cities.map((c) => (
              <option key={`o-${c.name}`} value={c.name}>{c.name}</option>
            ))}
          </select>
        </label>

        <button
          type="button"
          onClick={swap}
          data-testid={TID.calc.swap}
          aria-label="Swap"
          className="hidden md:flex h-11 w-11 items-center justify-center border border-brand-border text-brand-silver hover:text-brand-platinum hover:border-brand-platinum transition-colors"
        >
          <ArrowRightLeft size={16} />
        </button>

        <label className="flex flex-col gap-2">
          <span className="text-[10px] tracking-widest2 uppercase text-brand-silver flex items-center gap-2">
            <MapPin size={12} /> {t.calc.destination}
          </span>
          <select
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            data-testid={TID.calc.destination}
            className="bg-brand-bg border border-brand-border text-brand-platinum px-4 py-3 text-sm focus:border-brand-platinum"
          >
            {cities.map((c) => (
              <option key={`d-${c.name}`} value={c.name}>{c.name}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
        <label className="flex flex-col gap-2 md:col-span-2">
          <span className="text-[10px] tracking-widest2 uppercase text-brand-silver flex items-center gap-2">
            <Clock size={12} /> {t.calc.when}
          </span>
          <input
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            data-testid={TID.calc.when}
            className="bg-brand-bg border border-brand-border text-brand-platinum px-4 py-3 text-sm focus:border-brand-platinum"
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-[10px] tracking-widest2 uppercase text-brand-silver">{t.calc.service}</span>
          <select
            value={service}
            onChange={(e) => setService(e.target.value)}
            data-testid={TID.calc.service}
            className="bg-brand-bg border border-brand-border text-brand-platinum px-4 py-3 text-sm focus:border-brand-platinum"
          >
            <option value="transfer">{t.calc.transfer}</option>
            <option value="courier">{t.calc.courier}</option>
          </select>
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-[10px] tracking-widest2 uppercase text-brand-silver">Child seats</span>
          <select
            value={childSeats}
            onChange={(e) => setChildSeats(e.target.value)}
            data-testid={TID.calc.childSeats}
            className="bg-brand-bg border border-brand-border text-brand-platinum px-4 py-3 text-sm focus:border-brand-platinum"
          >
            {[0,1,2,3].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <label className="flex flex-col gap-2">
          <span className="text-[10px] tracking-widest2 uppercase text-brand-silver">Pets (up to 8 kg)</span>
          <select
            value={pet}
            onChange={(e) => setPet(e.target.value)}
            data-testid={TID.calc.pet}
            className="bg-brand-bg border border-brand-border text-brand-platinum px-4 py-3 text-sm focus:border-brand-platinum"
          >
            <option value="none">No pet</option>
            <option value="no_carrier">1 pet · no carrier</option>
            <option value="with_carrier">1 pet · with carrier</option>
          </select>
        </label>
        <div className="flex items-end">
          <button
            onClick={calculate}
            disabled={loading}
            data-testid={TID.calc.submit}
            className="btn-pill btn-pill--solid w-full justify-center"
          >
            {loading ? <><Loader2 size={14} className="animate-spin" /> {t.calc.calculating}</> : <>{t.calc.submit} <ArrowRight size={14} /></>}
          </button>
        </div>
      </div>

      {quote && (
        <div className="mt-10" data-testid={TID.calc.result}>
          <div className="flex items-baseline gap-6 mb-6 flex-wrap">
            <div>
              <p className="text-[10px] tracking-widest2 uppercase text-brand-silver">{t.calc.distance}</p>
              <p className="text-lg text-brand-platinum">{quote.distance_km} {t.common.km}</p>
            </div>
            <div>
              <p className="text-[10px] tracking-widest2 uppercase text-brand-silver">{t.calc.duration}</p>
              <p className="text-lg text-brand-platinum">≈ {quote.duration_min} {t.common.min}</p>
            </div>
            {quote.swissalpenline.hour_tier && quote.swissalpenline.hour_tier !== "normal" && (
              <span className="text-[10px] tracking-widest2 uppercase text-brand-platinum border border-brand-borderStrong px-3 py-1">
                {quote.swissalpenline.hour_tier} +{quote.swissalpenline.hour_supplement_pct}%
              </span>
            )}
            {quote.swissalpenline.weekend_applied && (
              <span className="text-[10px] tracking-widest2 uppercase text-brand-platinum border border-brand-borderStrong px-3 py-1">
                {t.calc.weekend}
              </span>
            )}
          </div>

          <p className="text-xs tracking-widest2 uppercase text-brand-silver mb-4">{t.calc.compare}</p>
          <div className="border border-brand-border divide-y divide-brand-border">
            {compareRows.map((row) => (
              <div
                key={row.key}
                data-testid={row.testid}
                className={`flex items-center justify-between px-5 py-4 ${
                  row.highlight ? "bg-brand-bg" : ""
                }`}
              >
                <span className={`text-sm ${row.highlight ? "text-brand-platinum font-medium" : "text-brand-silver"}`}>
                  {row.label}
                </span>
                <span className={`text-lg tabular-nums ${row.highlight ? "text-brand-platinum" : "text-brand-silver line-through opacity-70"}`}>
                  {row.value.toFixed(2)} {t.common.chf}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-6 border border-brand-border p-5">
            <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-3">— {t.calc.premium}</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              {t.calc.reasons.map((r) => (
                <li key={r} className="text-sm text-brand-platinum flex items-center gap-2">
                  <span className="inline-block h-1 w-1 bg-brand-platinum" /> {r}
                </li>
              ))}
            </ul>
          </div>

          <p className="text-[11px] text-brand-dim mt-4">{t.calc.currencyNote}</p>

          <button
            onClick={reserveFromQuote}
            data-testid={TID.calc.reserveFromQuote}
            className="btn-pill btn-pill--solid mt-6"
          >
            {t.calc.reserveNow} <ArrowRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default PriceCalculator;
